/**
 * Direct Supabase REST API client
 *
 * This bypasses the Supabase JS client for data operations to avoid
 * hanging issues caused by navigator.locks and BroadcastChannel problems.
 *
 * The Supabase JS client is ONLY used for:
 * - supabase.auth.* (with workarounds)
 * - supabase.channel() for realtime (if needed)
 *
 * All data operations (select, insert, update, delete) use direct fetch.
 */

// Get Supabase config from environment
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * Check if token is expired or about to expire (within 1 minute)
 * CRITICAL: Using 1 minute buffer to prevent excessive refresh attempts
 */
function isTokenExpired(expiresAt?: number): boolean {
  if (!expiresAt) return true;
  // Token expires_at is in seconds, Date.now() is in milliseconds
  const nowInSeconds = Math.floor(Date.now() / 1000);
  // Consider expired if within 1 minute (60 seconds) of expiry
  // This prevents refresh loops while still catching expiring tokens
  return nowInSeconds >= (expiresAt - 60);
}

/**
 * Get access token from localStorage
 * Bypasses Supabase auth client which can hang
 *
 * CRITICAL FIX: Now async - waits for token refresh to complete if token is expired
 */
export async function getAccessToken(): Promise<string | null> {
  try {
    const storageKey = 'sb-urkfvgqpzeuixgdayrex-auth-token';
    const stored = localStorage.getItem(storageKey);
    if (!stored) return null;
    const parsed = JSON.parse(stored);

    // Check if token is expired
    if (isTokenExpired(parsed.expires_at)) {
      console.warn('[supabaseRest] Token expired or expiring soon, refreshing...');

      // CRITICAL: Wait for refresh to complete before returning token
      const refreshed = await triggerTokenRefresh();

      if (refreshed) {
        // Re-read token from localStorage after refresh
        const newStored = localStorage.getItem(storageKey);
        if (!newStored) return null;
        const newParsed = JSON.parse(newStored);
        console.log('[supabaseRest] Returning refreshed token');
        return newParsed.access_token || null;
      } else {
        console.error('[supabaseRest] Token refresh failed, returning expired token');
        // Return expired token anyway - let the API return 401 so caller can handle
        return parsed.access_token || null;
      }
    }

    return parsed.access_token || null;
  } catch (e) {
    console.error('[supabaseRest] Error reading token:', e);
    return null;
  }
}

/**
 * Trigger a token refresh via direct REST API call
 * CRITICAL FIX: Bypasses Supabase JS client which hangs due to navigator.locks mocking
 *
 * The shared promise mechanism prevents concurrent refreshes - no cooldown needed.
 *
 * @returns Promise<boolean> - true if refresh succeeded, false otherwise
 */
let refreshInProgress = false;
let refreshPromise: Promise<boolean> | null = null;

async function triggerTokenRefresh(): Promise<boolean> {
  // If refresh already in progress, wait for it to complete
  // This shared promise prevents multiple concurrent refresh attempts
  if (refreshInProgress && refreshPromise) {
    console.log('[supabaseRest] Token refresh already in progress, waiting...');
    return refreshPromise;
  }

  refreshInProgress = true;

  // Create promise that can be shared between concurrent callers
  refreshPromise = (async (): Promise<boolean> => {
    try {
      console.log('[supabaseRest] Starting direct REST token refresh...');

      // Get current refresh token from localStorage
      const storageKey = 'sb-urkfvgqpzeuixgdayrex-auth-token';
      const stored = localStorage.getItem(storageKey);

      if (!stored) {
        console.error('[supabaseRest] No session in localStorage to refresh');
        return false;
      }

      const parsed = JSON.parse(stored);
      const refreshToken = parsed.refresh_token;

      if (!refreshToken) {
        console.error('[supabaseRest] No refresh token available');
        return false;
      }

      // Call Supabase token refresh endpoint directly
      // This bypasses the problematic Supabase JS client
      const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          refresh_token: refreshToken,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[supabaseRest] Token refresh failed:', response.status, errorText);
        return false;
      }

      const data = await response.json();

      if (!data.access_token || !data.refresh_token) {
        console.error('[supabaseRest] Token refresh returned invalid data');
        return false;
      }

      // Update localStorage with new tokens
      // Maintain the same structure as Supabase client uses
      const newSession = {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        expires_at: data.expires_at,
        expires_in: data.expires_in,
        token_type: data.token_type,
        user: data.user,
      };

      localStorage.setItem(storageKey, JSON.stringify(newSession));

      console.log('[supabaseRest] Token refreshed successfully via REST API, new expiry:',
        new Date(data.expires_at * 1000).toISOString());

      return true;

    } catch (e: any) {
      console.error('[supabaseRest] Token refresh exception:', e);
      return false;
    } finally {
      refreshInProgress = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Get current user ID from localStorage
 */
export function getUserId(): string | null {
  try {
    const storageKey = 'sb-urkfvgqpzeuixgdayrex-auth-token';
    const stored = localStorage.getItem(storageKey);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return parsed.user?.id || null;
  } catch (e) {
    console.error('[supabaseRest] Error reading user:', e);
    return null;
  }
}

/**
 * Build headers for Supabase REST API requests
 * CRITICAL FIX: Now async to await token refresh if needed
 */
async function buildHeaders(options?: { prefer?: string }): Promise<HeadersInit> {
  const token = await getAccessToken();
  const headers: HeadersInit = {
    'apikey': SUPABASE_ANON_KEY,
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (options?.prefer) {
    headers['Prefer'] = options.prefer;
  }

  return headers;
}

/**
 * Encode a value for use in query parameters
 */
function encodeQueryValue(value: any): string {
  if (value === null) return 'null';
  if (typeof value === 'string') return value;
  return String(value);
}

/**
 * Build query string from filters
 */
function buildQueryString(filters?: Record<string, any>): string {
  if (!filters || Object.keys(filters).length === 0) return '';

  const params: string[] = [];
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined) {
      params.push(`${encodeURIComponent(key)}=${encodeURIComponent(encodeQueryValue(value))}`);
    }
  }

  return params.length > 0 ? `&${params.join('&')}` : '';
}

export interface RestResponse<T> {
  data: T | null;
  error: { message: string; code?: string } | null;
}

/**
 * Wrapper to automatically retry failed requests after token refresh
 * If we get a 401, refresh the token and retry once
 *
 * The shared promise mechanism in triggerTokenRefresh prevents excessive refreshes,
 * so no additional cooldown is needed here.
 */
async function withTokenRetry<T>(
  operation: () => Promise<RestResponse<T>>
): Promise<RestResponse<T>> {
  const result = await operation();

  // If we got a 401 (JWT expired), refresh token and retry once
  if (result.error?.code === 'PGRST303' || result.error?.code === '401' || result.error?.message?.includes('JWT expired')) {
    console.warn('[supabaseRest] Got 401 error, attempting token refresh and retry...');

    const refreshed = await triggerTokenRefresh();

    if (refreshed) {
      console.log('[supabaseRest] Token refreshed, retrying operation once...');
      // Retry the operation once with new token
      return operation();
    } else {
      console.error('[supabaseRest] Token refresh failed, cannot retry');
      // Return the original error
      return result;
    }
  }

  return result;
}

/**
 * SELECT query - fetch records from a table
 *
 * @param table Table name
 * @param options Query options
 * @returns Promise with data array or error
 *
 * @example
 * // Simple select all
 * const { data, error } = await restSelect('objects');
 *
 * @example
 * // Select with filters
 * const { data, error } = await restSelect('sync_queue', {
 *   select: 'id,table_name,operation',
 *   eq: { user_id: 'xxx', synced: false },
 *   order: 'timestamp.asc',
 *   limit: 100
 * });
 *
 * @example
 * // Select with OR condition
 * const { data, error } = await restSelect('objects', {
 *   select: '*',
 *   eq: { user_id: 'xxx' },
 *   or: 'updated_at.gte.2024-01-01,created_at.gte.2024-01-01'
 * });
 */
export async function restSelect<T = any>(
  table: string,
  options?: {
    select?: string;
    eq?: Record<string, any>;
    neq?: Record<string, any>;
    gt?: Record<string, any>;
    gte?: Record<string, any>;
    lt?: Record<string, any>;
    lte?: Record<string, any>;
    is?: Record<string, null>;
    not?: Record<string, any>;
    in?: Record<string, any[]>;
    or?: string;
    order?: string;
    limit?: number;
    offset?: number;
    single?: boolean;
  }
): Promise<RestResponse<T[]>> {
  // Wrap with automatic retry on 401
  return withTokenRetry(async () => {
  try {
    const select = options?.select || '*';
    let url = `${SUPABASE_URL}/rest/v1/${table}?select=${encodeURIComponent(select)}`;

    // Add eq filters (column=eq.value)
    if (options?.eq) {
      for (const [col, val] of Object.entries(options.eq)) {
        url += `&${col}=eq.${encodeURIComponent(encodeQueryValue(val))}`;
      }
    }

    // Add neq filters (column=neq.value)
    if (options?.neq) {
      for (const [col, val] of Object.entries(options.neq)) {
        url += `&${col}=neq.${encodeURIComponent(encodeQueryValue(val))}`;
      }
    }

    // Add gt filters (column=gt.value)
    if (options?.gt) {
      for (const [col, val] of Object.entries(options.gt)) {
        url += `&${col}=gt.${encodeURIComponent(encodeQueryValue(val))}`;
      }
    }

    // Add gte filters (column=gte.value)
    if (options?.gte) {
      for (const [col, val] of Object.entries(options.gte)) {
        url += `&${col}=gte.${encodeURIComponent(encodeQueryValue(val))}`;
      }
    }

    // Add lt filters (column=lt.value)
    if (options?.lt) {
      for (const [col, val] of Object.entries(options.lt)) {
        url += `&${col}=lt.${encodeURIComponent(encodeQueryValue(val))}`;
      }
    }

    // Add lte filters (column=lte.value)
    if (options?.lte) {
      for (const [col, val] of Object.entries(options.lte)) {
        url += `&${col}=lte.${encodeURIComponent(encodeQueryValue(val))}`;
      }
    }

    // Add is filters (column=is.null)
    if (options?.is) {
      for (const [col, val] of Object.entries(options.is)) {
        url += `&${col}=is.${val === null ? 'null' : encodeURIComponent(String(val))}`;
      }
    }

    // Add not filters (column=not.is.null)
    if (options?.not) {
      for (const [col, val] of Object.entries(options.not)) {
        if (val === null) {
          url += `&${col}=not.is.null`;
        } else {
          url += `&${col}=not.eq.${encodeURIComponent(encodeQueryValue(val))}`;
        }
      }
    }

    // Add in filters (column=in.(val1,val2))
    if (options?.in) {
      for (const [col, vals] of Object.entries(options.in)) {
        const valList = vals.map(v => encodeURIComponent(encodeQueryValue(v))).join(',');
        url += `&${col}=in.(${valList})`;
      }
    }

    // Add or filter
    if (options?.or) {
      url += `&or=(${encodeURIComponent(options.or)})`;
    }

    // Add order
    if (options?.order) {
      url += `&order=${encodeURIComponent(options.order)}`;
    }

    // Add limit
    if (options?.limit) {
      url += `&limit=${options.limit}`;
    }

    // Add offset
    if (options?.offset) {
      url += `&offset=${options.offset}`;
    }

    const headers = await buildHeaders();
    if (options?.single) {
      headers['Accept'] = 'application/vnd.pgrst.object+json';
    }

    const response = await fetch(url, { headers });

    if (!response.ok) {
      const errorText = await response.text();
      let errorObj;
      try {
        errorObj = JSON.parse(errorText);
      } catch {
        errorObj = { message: errorText };
      }
      return {
        data: null,
        error: {
          message: errorObj.message || errorObj.error || errorText,
          code: errorObj.code || response.status.toString()
        }
      };
    }

    const data = await response.json();
    return { data: Array.isArray(data) ? data : [data], error: null };

  } catch (e: any) {
    return {
      data: null,
      error: { message: e.message || String(e) }
    };
  }
  });
}

/**
 * SELECT all records with automatic pagination.
 * PostgREST has a default row limit (typically 1000). This function
 * paginates through all pages to ensure no rows are missed.
 */
export async function restSelectAll<T = any>(
  table: string,
  options?: Parameters<typeof restSelect>[1]
): Promise<RestResponse<T[]>> {
  const PAGE_SIZE = 1000;
  let allData: T[] = [];
  let offset = 0;

  while (true) {
    const { data, error } = await restSelect<T>(table, {
      ...options,
      limit: PAGE_SIZE,
      offset,
    });
    if (error) return { data: allData.length > 0 ? allData : null, error };
    if (!data || data.length === 0) break;
    allData = allData.concat(data);
    if (data.length < PAGE_SIZE) break; // Last page
    offset += PAGE_SIZE;
  }

  return { data: allData, error: null };
}

/**
 * SELECT single record
 */
export async function restSelectSingle<T = any>(
  table: string,
  options?: Parameters<typeof restSelect>[1]
): Promise<RestResponse<T>> {
  const result = await restSelect<T>(table, { ...options, limit: 1 });
  if (result.error) {
    return { data: null, error: result.error };
  }
  if (!result.data || result.data.length === 0) {
    return { data: null, error: { message: 'No rows returned', code: 'PGRST116' } };
  }
  return { data: result.data[0], error: null };
}

/**
 * INSERT records into a table
 *
 * @param table Table name
 * @param data Record or array of records to insert
 * @param options Insert options
 * @returns Promise with inserted data or error
 *
 * @example
 * const { data, error } = await restInsert('objects', {
 *   id: 'xxx',
 *   name: 'Test',
 *   type: 'note'
 * });
 */
export async function restInsert<T = any>(
  table: string,
  data: Record<string, any> | Record<string, any>[],
  options?: {
    returning?: boolean;
    onConflict?: string;
    upsert?: boolean;
  }
): Promise<RestResponse<T[]>> {
  // Wrap with automatic retry on 401
  return withTokenRetry(async () => {
  try {
    const url = `${SUPABASE_URL}/rest/v1/${table}`;

    // Build prefer header
    const preferParts: string[] = [];
    if (options?.returning !== false) {
      preferParts.push('return=representation');
    } else {
      preferParts.push('return=minimal');
    }
    if (options?.upsert) {
      preferParts.push('resolution=merge-duplicates');
    }

    const headers = await buildHeaders({ prefer: preferParts.join(',') });

    // Add on-conflict header for upsert
    if (options?.onConflict) {
      headers['On-Conflict'] = options.onConflict;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorObj;
      try {
        errorObj = JSON.parse(errorText);
      } catch {
        errorObj = { message: errorText };
      }
      return {
        data: null,
        error: {
          message: errorObj.message || errorObj.error || errorText,
          code: errorObj.code || response.status.toString()
        }
      };
    }

    // Handle empty response (return=minimal)
    if (options?.returning === false) {
      return { data: [] as unknown as T[], error: null };
    }

    const responseText = await response.text();
    if (!responseText) {
      return { data: [] as unknown as T[], error: null };
    }

    const result = JSON.parse(responseText);
    return { data: Array.isArray(result) ? result : [result], error: null };

  } catch (e: any) {
    return {
      data: null,
      error: { message: e.message || String(e) }
    };
  }
  });
}

/**
 * UPDATE records in a table
 *
 * @param table Table name
 * @param data Fields to update
 * @param filters Filter conditions (eq, in, etc.)
 * @returns Promise with updated data or error
 *
 * @example
 * const { data, error } = await restUpdate('sync_queue',
 *   { synced: true },
 *   { eq: { id: 'xxx' } }
 * );
 */
export async function restUpdate<T = any>(
  table: string,
  data: Record<string, any>,
  filters: {
    eq?: Record<string, any>;
    in?: Record<string, any[]>;
    not?: Record<string, any>;
  },
  options?: {
    returning?: boolean;
  }
): Promise<RestResponse<T[]>> {
  // Wrap with automatic retry on 401
  return withTokenRetry(async () => {
  try {
    let url = `${SUPABASE_URL}/rest/v1/${table}?`;

    // Add eq filters
    if (filters.eq) {
      for (const [col, val] of Object.entries(filters.eq)) {
        url += `${col}=eq.${encodeURIComponent(encodeQueryValue(val))}&`;
      }
    }

    // Add in filters
    if (filters.in) {
      for (const [col, vals] of Object.entries(filters.in)) {
        const valList = vals.map(v => encodeURIComponent(encodeQueryValue(v))).join(',');
        url += `${col}=in.(${valList})&`;
      }
    }

    // Add not filters
    if (filters.not) {
      for (const [col, val] of Object.entries(filters.not)) {
        if (val === null) {
          url += `${col}=not.is.null&`;
        } else {
          url += `${col}=not.eq.${encodeURIComponent(encodeQueryValue(val))}&`;
        }
      }
    }

    // Remove trailing &
    url = url.replace(/&$/, '');

    const prefer = options?.returning !== false ? 'return=representation' : 'return=minimal';
    const headers = await buildHeaders({ prefer });

    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorObj;
      try {
        errorObj = JSON.parse(errorText);
      } catch {
        errorObj = { message: errorText };
      }
      return {
        data: null,
        error: {
          message: errorObj.message || errorObj.error || errorText,
          code: errorObj.code || response.status.toString()
        }
      };
    }

    if (options?.returning === false) {
      return { data: [] as unknown as T[], error: null };
    }

    const responseText = await response.text();
    if (!responseText) {
      return { data: [] as unknown as T[], error: null };
    }

    const result = JSON.parse(responseText);
    return { data: Array.isArray(result) ? result : [result], error: null };

  } catch (e: any) {
    return {
      data: null,
      error: { message: e.message || String(e) }
    };
  }
  });
}

/**
 * DELETE records from a table
 *
 * @param table Table name
 * @param filters Filter conditions
 * @returns Promise with deleted data or error
 *
 * @example
 * const { error } = await restDelete('sync_queue', { eq: { id: 'xxx' } });
 */
export async function restDelete<T = any>(
  table: string,
  filters: {
    eq?: Record<string, any>;
    in?: Record<string, any[]>;
    not?: Record<string, any>;
  },
  options?: {
    returning?: boolean;
  }
): Promise<RestResponse<T[]>> {
  // Wrap with automatic retry on 401
  return withTokenRetry(async () => {
  try {
    let url = `${SUPABASE_URL}/rest/v1/${table}?`;

    // Add eq filters
    if (filters.eq) {
      for (const [col, val] of Object.entries(filters.eq)) {
        url += `${col}=eq.${encodeURIComponent(encodeQueryValue(val))}&`;
      }
    }

    // Add in filters
    if (filters.in) {
      for (const [col, vals] of Object.entries(filters.in)) {
        const valList = vals.map(v => encodeURIComponent(encodeQueryValue(v))).join(',');
        url += `${col}=in.(${valList})&`;
      }
    }

    // Add not filters
    if (filters.not) {
      for (const [col, val] of Object.entries(filters.not)) {
        if (val === null) {
          url += `${col}=not.is.null&`;
        } else {
          url += `${col}=not.eq.${encodeURIComponent(encodeQueryValue(val))}&`;
        }
      }
    }

    // Remove trailing &
    url = url.replace(/&$/, '');

    const prefer = options?.returning !== false ? 'return=representation' : 'return=minimal';
    const headers = await buildHeaders({ prefer });

    const response = await fetch(url, {
      method: 'DELETE',
      headers
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorObj;
      try {
        errorObj = JSON.parse(errorText);
      } catch {
        errorObj = { message: errorText };
      }
      return {
        data: null,
        error: {
          message: errorObj.message || errorObj.error || errorText,
          code: errorObj.code || response.status.toString()
        }
      };
    }

    if (options?.returning === false) {
      return { data: [] as unknown as T[], error: null };
    }

    const responseText = await response.text();
    if (!responseText) {
      return { data: [] as unknown as T[], error: null };
    }

    const result = JSON.parse(responseText);
    return { data: Array.isArray(result) ? result : [result], error: null };

  } catch (e: any) {
    return {
      data: null,
      error: { message: e.message || String(e) }
    };
  }
  });
}

/**
 * Expose utilities on window for debugging
 */
if (typeof window !== 'undefined') {
  (window as any).supabaseRest = {
    restSelect,
    restSelectSingle,
    restInsert,
    restUpdate,
    restDelete,
    getUserId,
    getAccessToken
  };
}
